import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import './UserProfile.css';
import { firestore } from '../firebaseConfig';
import { getOrCreateDirectConversation } from '../firebase/messagingUtils';

const privateNoteDraftCache = new Map();

export default function UserProfile() {
  const { userId } = useParams();            // profile you're viewing
  const navigate = useNavigate();
  const auth = getAuth();
  const [userData, setUserData] = useState(null);
  const [isLaunchingDM, setIsLaunchingDM] = useState(false);
  const [privateNote, setPrivateNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);
  const [showNoteInfo, setShowNoteInfo] = useState(false);
  const noteSectionRef = React.useRef(null);
  const PRIVATE_NOTE_CHAR_LIMIT = 750;
  const currentUid = auth.currentUser?.uid;
  const noteDraftCacheKey = useMemo(() => (
    currentUid && userId && currentUid !== userId ? `${currentUid}:${userId}` : null
  ), [currentUid, userId]);

  useEffect(() => {
    setIsNoteExpanded(false);
    setIsNoteEditing(false);
    setShowNoteInfo(false);
  }, [userId]);

 useEffect(() => {
  const fetchProfile = async () => {
    try {
      const userRef = doc(firestore, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();

        // 🔹 Fetch subcollections (experience, certifications, languages)
        const [expSnap, certSnap, languagesSnap] = await Promise.all([
          getDocs(collection(firestore, 'users', userId, 'experience')),
          getDocs(collection(firestore, 'users', userId, 'certifications')),
          getDocs(collection(firestore, 'users', userId, 'languagesSpoken'))
        ]);

        const experience = expSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const certifications = certSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const languagesSpoken = languagesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

experience.sort((a, b) => {
  if (typeof a.position === 'number' && typeof b.position === 'number')
    return a.position - b.position; // use stored order
  // fallback: sort newest first by endDate
  const parseDate = (str) => {
    if (!str) return new Date(0);
    if (typeof str === 'string' && str.toLowerCase().includes('present')) return new Date();
    const [mm, yyyy] = str.split('/');
    return new Date(parseInt(yyyy) || 0, (parseInt(mm) || 1) - 1, 1);
  };
  return parseDate(b.endDate) - parseDate(a.endDate);
});
        setUserData({ ...data, experience, certifications, languagesSpoken });
      } else {
        console.warn('User profile not found.');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  fetchProfile();
}, [userId]);

  useEffect(() => {
    const fetchPrivateNote = async () => {
      if (!currentUid || !userId || currentUid === userId) {
        setPrivateNote(null);
        setNoteDraft('');
        return;
      }

      try {
        const noteRef = doc(firestore, 'users', currentUid, 'profileNotes', userId);
        const noteSnap = await getDoc(noteRef);
        if (noteSnap.exists()) {
          const data = noteSnap.data();
          const savedText = data.text || '';
          const cachedDraft = noteDraftCacheKey && privateNoteDraftCache.has(noteDraftCacheKey)
            ? privateNoteDraftCache.get(noteDraftCacheKey)
            : null;
          setPrivateNote({ id: noteSnap.id, ...data });
          setNoteDraft(cachedDraft !== null ? cachedDraft : savedText);
        } else {
          const cachedDraft = noteDraftCacheKey && privateNoteDraftCache.has(noteDraftCacheKey)
            ? privateNoteDraftCache.get(noteDraftCacheKey)
            : null;
          setPrivateNote(null);
          setNoteDraft(cachedDraft !== null ? cachedDraft : '');
        }
      } catch (error) {
        console.error('Error fetching private note:', error);
      }
    };

    fetchPrivateNote();
  }, [currentUid, noteDraftCacheKey, userId]);

  useEffect(() => {
    if (isNoteEditing || (!isNoteExpanded && !showNoteInfo)) return undefined;

    const handleOutside = (event) => {
      if (noteSectionRef.current && noteSectionRef.current.contains(event.target)) return;
      setIsNoteExpanded(false);
      setShowNoteInfo(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isNoteEditing, isNoteExpanded, showNoteInfo]);


  const displayName = useMemo(() => {
    if (!userData) return 'N/A';
    if (userData.firstInitialLastName) return userData.firstInitialLastName;
    const f = (userData.firstName || '').trim();
    const l = (userData.lastName || '').trim();
    if (f && l) return `${f[0].toLowerCase()}.${l.toLowerCase()}`;
    return 'N/A';
  }, [userData]);

	  const joinedDate = useMemo(() => {
    if (!userData?.joined?.toDate) return null;
    try {
      const dateObj = userData.joined.toDate(); // Firestore Timestamp → JS Date
      return dateObj.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  }, [userData]);


  const handleMessage = async () => {
  if (!auth.currentUser) {
    alert('Please sign in to start a conversation.');
    return;
  }
  if (!userId || auth.currentUser.uid === userId) return;

  setIsLaunchingDM(true);
  try {
    const uidA = auth.currentUser.uid;
    const uidB = userId;
    const myRef = doc(firestore, 'users', uidA);
    const otherRef = doc(firestore, 'users', uidB);
    const [mySnap, otherSnap] = await Promise.all([getDoc(myRef), getDoc(otherRef)]);
    const me = mySnap.exists() ? mySnap.data() : {};
    const other = otherSnap.exists() ? otherSnap.data() : {};

    const convoId = await getOrCreateDirectConversation(uidA, uidB, {
      memberProfiles: {
        [uidA]: {
          firstInitialLastName: me.firstInitialLastName || null,
          country: me.country || null,
          stateProvince: me.stateProvince || me.state || null,
          city: me.city || null,
          avatarUrl: me.avatarUrl || null,
        },
        [uidB]: {
          firstInitialLastName: other.firstInitialLastName || null,
          country: other.country || null,
          stateProvince: other.stateProvince || other.state || null,
          city: other.city || null,
          avatarUrl: other.avatarUrl || null,
        },
      },
    });

    navigate(`/direct/${convoId}`, { state: { fromProfile: true } });

  } catch (e) {
    console.error('Failed to open/start DM:', e);
    alert('Could not start the conversation. Please try again.');
  } finally {
    setIsLaunchingDM(false);
  }
};

  const handleOpenNoteEditor = () => {
    const cachedDraft = noteDraftCacheKey && privateNoteDraftCache.has(noteDraftCacheKey)
      ? privateNoteDraftCache.get(noteDraftCacheKey)
      : null;
    setNoteDraft(cachedDraft !== null ? cachedDraft : privateNote?.text || '');
    setIsNoteEditing(true);
    setIsNoteExpanded(true);
  };

  const handleCancelNote = () => {
    if (noteDraftCacheKey) privateNoteDraftCache.delete(noteDraftCacheKey);
    setNoteDraft(privateNote?.text || '');
    setIsNoteEditing(false);
    setIsNoteExpanded(false);
  };

  const handleSavePrivateNote = async () => {
    const currentUid = auth.currentUser?.uid;
    if (!currentUid || !userId || currentUid === userId) return;

    const trimmed = noteDraft.trim();
    if (!trimmed) {
      alert('Please enter a note before saving.');
      return;
    }

    try {
      const noteRef = doc(firestore, 'users', currentUid, 'profileNotes', userId);
      const payload = {
        targetUserId: userId,
        targetDisplayName: displayName,
        targetCountry: userData?.country || null,
        targetStateProvince: userData?.stateProvince || userData?.state || null,
        targetCity: userData?.city || null,
        text: trimmed,
        updatedAt: serverTimestamp(),
      };

      await setDoc(
        noteRef,
        privateNote
          ? payload
          : { ...payload, createdAt: serverTimestamp() },
        { merge: true }
      );

      setPrivateNote({ id: userId, ...payload, text: trimmed });
      if (noteDraftCacheKey) privateNoteDraftCache.delete(noteDraftCacheKey);
      setNoteDraft(trimmed);
      setIsNoteEditing(false);
      setIsNoteExpanded(false);
    } catch (error) {
      console.error('Error saving private note:', error);
      alert('Could not save the private note. Please try again.');
    }
  };

  const noteFirstLine = useMemo(() => {
    const text = privateNote?.text || '';
    return text.split(/\r?\n/).find((line) => line.trim()) || text.trim();
  }, [privateNote?.text]);

  const noteHasMultipleLines = useMemo(() => {
    const text = privateNote?.text || '';
    return text.split(/\r?\n/).filter((line) => line.trim()).length > 1;
  }, [privateNote?.text]);

  const noteDraftAtLimit = noteDraft.length >= PRIVATE_NOTE_CHAR_LIMIT;


  if (!userData) return <p>Loading profile...</p>;

  return (
  <div className="profile-container">
  <div className="profile-avatar">
    <img
      src={userData.avatarUrl || '/avatar-image.png'}
      alt="Profile"
      style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover' }}
    />
  </div>

  <h2 className="profile-title"><strong>Member Profile</strong></h2>
  {userData.isFederal === true && (
    <div className="profile-fed-badge-row">
      <span className="profile-fed-badge" title="Federal employee">F</span>
    </div>
  )}

  <div className="profile-data">
   <div className="profile-info-block">
    <div className="profile-row">
      <strong>Name:</strong>
      <span>{displayName}</span>
    </div>
    <div className="profile-row">
      <strong>Country:</strong>
      <span>{userData.country || 'N/A'}</span>
    </div>
    <div className="profile-row">
      <strong>State / Province:</strong>
      <span>{userData.stateProvince || userData.state || 'N/A'}</span>
    </div>
    <div className="profile-row">
      <strong>City:</strong>
      <span>{userData.city || 'N/A'}</span>
    </div>
    <div className="profile-row">
  <strong>Joined:</strong>
  <span>{joinedDate || 'N/A'}</span>
   </div>
    </div>

    <button
      onClick={handleMessage}
      disabled={isLaunchingDM || (getAuth().currentUser?.uid === userId)}
      className="message-button"
      title={getAuth().currentUser?.uid === userId ? 'This is you' : 'Start Direct Message'}
    >
      {isLaunchingDM ? 'Opening…' : 'Message'}
    </button>

    {getAuth().currentUser?.uid !== userId && (
      <div className="private-note-section" ref={noteSectionRef}>
        {!privateNote && !isNoteEditing && (
          <div className="private-note-empty-row">
            <button className="private-note-button" onClick={handleOpenNoteEditor}>
              + Private Note
            </button>
            <button
              className="private-note-info-button"
              onClick={() => setShowNoteInfo((prev) => !prev)}
              title="About private notes"
              aria-label="About private notes"
              type="button"
            >
              i
            </button>
          </div>
        )}

        {privateNote && !isNoteEditing && (
          <div className={`private-note-card${isNoteExpanded ? ' expanded' : ''}`}>
            <div className="private-note-card-header">
              <button
                className="private-note-card-title"
                onClick={() => setIsNoteExpanded((prev) => !prev)}
                type="button"
              >
                📝
                Private Note 
              </button>
              <button
                className="private-note-info-button"
                onClick={() => setShowNoteInfo((prev) => !prev)}
                title="About private notes"
                aria-label="About private notes"
                type="button"
              >
                i
              </button>
              <button className="private-note-edit-button" onClick={handleOpenNoteEditor} type="button">
                Edit
              </button>
            </div>
            <button
              className="private-note-preview"
              onClick={() => setIsNoteExpanded((prev) => !prev)}
              type="button"
            >
              {isNoteExpanded && noteHasMultipleLines ? privateNote.text : noteFirstLine}
            </button>
          </div>
        )}

        {isNoteEditing && (
          <div className="private-note-editor">
            <div className="private-note-editor-header">
              <strong>Private Note</strong>
              <button
                className="private-note-info-button"
                onClick={() => setShowNoteInfo((prev) => !prev)}
                title="About private notes"
                aria-label="About private notes"
                type="button"
              >
                i
              </button>
            </div>
            <textarea
              value={noteDraft}
              onChange={(event) => {
                const nextDraft = event.target.value.slice(0, PRIVATE_NOTE_CHAR_LIMIT);
                setNoteDraft(nextDraft);
                if (noteDraftCacheKey) privateNoteDraftCache.set(noteDraftCacheKey, nextDraft);
              }}
              placeholder="Write a private note about this person..."
              maxLength={PRIVATE_NOTE_CHAR_LIMIT}
              rows={5}
            />
            <div className={`private-note-count${noteDraftAtLimit ? ' limit-reached' : ''}`}>
              {noteDraft.length}/{PRIVATE_NOTE_CHAR_LIMIT}
            </div>
            <div className="private-note-actions">
              <button className="private-note-save-button" onClick={handleSavePrivateNote}>
                Save
              </button>
              <button className="private-note-cancel-button" onClick={handleCancelNote}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {showNoteInfo && (
          <div className="private-note-info-popover">
            <p><strong>Private notes are only visible to you.</strong></p>
            <p>
              Use this note for anything you want to remember about this profile (i.e. how you
              know this person, where you met, prior conversations...)
            </p>
            <p><u>Only you can see this note</u>. It is not visible to this person or anyone else.</p>
          </div>
        )}
      </div>
    )}

  {userData.aboutMe && userData.aboutMe.trim() && (
    <div className="about-me-section">
      <h3 className="section-title">About Me</h3>
      <p className="about-me-text">{userData.aboutMe}</p>
    </div>
  )}

	{/* --- MAIN TWO-COLUMN LAYOUT --- */}
<div className="profile-main-sections">

  {/* LEFT COLUMN – Experience */}
  <div className="left-column">
    <h3 className="section-title">
  Law Enforcement Experience
  {userData.totalYearsOfService && (
    <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#444' }}>
      – <strong style={{ fontSize: '1em', color: '#000' }}>
        {Number(userData.totalYearsOfService).toFixed(1)}
      </strong> years
    </span>
  )}
</h3>

    {userData.experience?.length ? (
      userData.experience.map((exp) => (
        <div key={exp.id} className="experience-card">
          <div><strong>{exp.department}</strong>{exp.state ? ` (${exp.state})` : ''}</div>
          <div>{exp.startDate} - {exp.endDate || 'Present'}</div>
        </div>
      ))
    ) : (
      <p className="empty-text">No experience listed.</p>
    )}
  </div>

  {/* RIGHT COLUMN – Certifications & Recommendations */}
  <div className="right-column">
    <h3 className="section-title">Certifications</h3>
    {userData.certifications?.length ? (
      <div className="certifications-list">
        {userData.certifications.map((cert) => (
          <div key={cert.id} className="cert-badge">
            {cert.name}
          </div>
        ))}
      </div>
    ) : (
      <p className="empty-text">No certifications listed.</p>
    )}

    {userData.languagesSpoken?.length > 0 && (
      <>
        <h3 className="section-title">Languages Spoken</h3>
        <div className="languages-card">
          {userData.languagesSpoken.map((language) => (
            <span key={language.id} className="language-badge">{language.name}</span>
          ))}
        </div>
      </>
    )}
 	     </div>
	  </div>
      </div>
   </div>
 );
}
